import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as caricoRepartiApi from "api/schedulazione/caricoRepartiApi";

// ============================================
// INTERFACES
// ============================================

// --- Panoramica (tutti i reparti) ---

export interface CaricoRepartoGiorno {
    reparto_id: number;
    codice: string;
    descrizione: string;
    colore?: string;
    ore_disponibili: number;
    ore_schedulate: number;
    allarme: boolean;
    placeholder_count?: number;       // numero placeholder attivi sul reparto in quel giorno
}

export interface GiornoCalendario {
    data: string;
    is_lavorativo: boolean;
    reparti: CaricoRepartoGiorno[];
}

export interface CaricoRepartiPanoramica {
    data_da: string;
    data_a: string;
    giorni: number;
    calendario: GiornoCalendario[];
}

// --- Dettaglio (singolo reparto con operatori) ---

export interface CaricoOperatoreGiorno {
    operatore_id: number;
    codice: string;
    nominativo: string;
    ore_disponibili: number;
    ore_schedulate: number;
    assente: boolean;
    allarme: boolean;
}

export interface PlaceholderInfo {
    tipo: "reparto" | "spostamento_entrante" | "spostamento_uscente";
    ore: number;                       // segno già applicato (uscente è negativo)
    operatore_id?: number | null;
    operatore_display?: string | null;
    note?: string | null;
}

export interface GiornoCalendarioDettaglio {
    data: string;
    is_lavorativo: boolean;
    ore_disponibili: number;
    ore_schedulate: number;
    allarme: boolean;
    operatori: CaricoOperatoreGiorno[];
    placeholders?: PlaceholderInfo[];
}

export interface CaricoRepartoDettaglio {
    reparto_id: number;
    codice: string;
    descrizione: string;
    colore?: string;
    data_da: string;
    data_a: string;
    giorni: number;
    calendario: GiornoCalendarioDettaglio[];
}

// ============================================
// STATO SLICE
// ============================================

export interface CaricoRepartiState {
    panoramica: CaricoRepartiPanoramica | null;
    panoramicaErrorsStack: ErrorsStack;
    dettaglio: { [repartoId: number]: CaricoRepartoDettaglio };
    dettaglioErrorsStack: ErrorsStack;
}

const initialState: CaricoRepartiState = {
    panoramica: null,
    panoramicaErrorsStack: { status: ErrorStatusTypes.OK },
    dettaglio: {},
    dettaglioErrorsStack: { status: ErrorStatusTypes.OK },
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchCaricoReparti = createAsyncThunk(
    "caricoReparti/fetchCaricoReparti",
    async ({ dataDa, giorni }: { dataDa: string; giorni: number }) => {
        return await caricoRepartiApi.fetchCaricoReparti(dataDa, giorni);
    }
);

export const fetchCaricoReparto = createAsyncThunk(
    "caricoReparti/fetchCaricoReparto",
    async ({ dataDa, giorni, repartoId }: { dataDa: string; giorni: number; repartoId: number }) => {
        return await caricoRepartiApi.fetchCaricoReparto(dataDa, giorni, repartoId);
    }
);

// ============================================
// SLICE
// ============================================

export const caricoRepartiSlice = createSlice({
    name: "caricoReparti",
    initialState,
    reducers: {
        resetCaricoReparti: (state) => {
            state.panoramica = null;
            state.panoramicaErrorsStack = { status: ErrorStatusTypes.OK };
            state.dettaglio = {};
            state.dettaglioErrorsStack = { status: ErrorStatusTypes.OK };
        },
        resetCaricoDettaglio: (state) => {
            state.dettaglio = {};
            state.dettaglioErrorsStack = { status: ErrorStatusTypes.OK };
        },
    },
    extraReducers: (builder) => {
        // --- Panoramica ---
        builder.addCase(fetchCaricoReparti.pending, (state) => {
            state.panoramicaErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchCaricoReparti.fulfilled, (state, action) => {
            state.panoramica = action.payload;
            state.panoramicaErrorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchCaricoReparti.rejected, (state, action) => {
            state.panoramicaErrorsStack = parseErrorMessage(action.error);
        });

        // --- Dettaglio reparto ---
        builder.addCase(fetchCaricoReparto.pending, (state) => {
            state.dettaglioErrorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchCaricoReparto.fulfilled, (state, action) => {
            const data = action.payload as CaricoRepartoDettaglio;
            state.dettaglio[data.reparto_id] = data;
            state.dettaglioErrorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchCaricoReparto.rejected, (state, action) => {
            state.dettaglioErrorsStack = parseErrorMessage(action.error);
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const { resetCaricoReparti, resetCaricoDettaglio } = caricoRepartiSlice.actions;

export const caricoRepartiSliceReducer = caricoRepartiSlice.reducer;
